import { useKmagic } from './useKmagic';

/**
 * kMagic - an AI art director. Runs entirely in the background (internals live
 * on /admin); the tab shows only a progress bar and, when done, a result the
 * user can preview, apply (overwrite) or roll back to their own version.
 */
export function StudioKmagicPanel() {
  const {
    projectId, instruction, setInstruction, phase, inPreview, pct, quotaError, runningMessage,
    result: r, start, showResult, applyResult, rollback, reset, runAgain,
  } = useKmagic();

  return (
    <div className="slp-body kmg-panel-live">
      {(phase === 'idle' || phase === 'running') && (
        <>
          <div className="kmg-intro">
            <KmagicMark size={40} />
            <p>AI art-directs your whole edit.</p>
          </div>
          <label className="kmg-label">Custom instruction (optional)</label>
          <textarea
            className="kmg-instruction"
            placeholder="e.g. keep it punchy, highlight the numbers, calm music…"
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            disabled={phase === 'running'}
            rows={3}
          />
          {phase !== 'running' ? (
            <button className="kmg-apply" onClick={start} disabled={!projectId}>
              ✦ Apply magic
            </button>
          ) : (
            <div className="kmg-progress-wrap">
              <div className="kmg-progress">
                <div className="kmg-progress-fill" style={{ width: `${Math.max(6, pct)}%` }} />
              </div>
              <div className="kmg-progress-row">
                <span className="kmg-status" key={runningMessage}>{runningMessage}</span>
                <span className="kmg-pct">{pct}%</span>
              </div>
              <p className="kmg-hint">Working its magic — you can keep editing.</p>
            </div>
          )}
          {quotaError && (
            <p className="kmg-hint" style={{ color: '#c2503f' }}>
              {quotaError} <a href="/billing" style={{ color: '#c25e83', fontWeight: 600 }}>Upgrade →</a>
            </p>
          )}
          {!projectId && <p className="kmg-hint">Open a project to use kMagic.</p>}
          {phase !== 'running' && projectId && (
            <button className="kmg-reset" onClick={reset} title="Restore the original template, breaks and emphasis; clear all customizations">
              ↺ Reset to original
            </button>
          )}
        </>
      )}

      {(phase === 'ready' || phase === 'previewing') && r && (
        <div className="kmg-result">
          <div className="kmg-result-head">
            <span className="kmg-badge">✦ Result ready</span>
          </div>
          {r.rationale && <p className="kmg-rationale">{r.rationale}</p>}
          <ul className="kmg-summary">
            {r.templateId && <li><b>Style</b><span>{r.templateId}</span></li>}
            <li><b>Emphasis</b><span>{r.emphasisWordIds?.length ?? 0} words</span></li>
            <li>
              <b>Hooks</b>
              <span>
                {r.hooks?.length
                  ? `${r.hooks.length} takeover${r.hooks.length > 1 ? 's' : ''} · ${r.hooks.map((h) => h.text).join(', ')}`
                  : '-'}
              </span>
            </li>
            <li><b>Transitions</b><span>{r.transitions?.length ?? 0}</span></li>
            <li>
              <b>B-roll</b>
              <span>
                {r.brolls?.length
                  ? `${r.brolls.length} clip${r.brolls.length > 1 ? 's' : ''} · ${r.brolls.map((b) => b.keyword).join(', ')}`
                  : r.brollKeywords?.length
                    ? `no clip found · tried: ${r.brollKeywords.map((b) => b.keyword).join(', ')}`
                    : '-'}
              </span>
            </li>
            <li>
              <b>Music</b>
              <span>
                {r.music?.name
                  ? r.music.name
                  : r.musicKeywords?.length
                    ? `no track found · tried: ${r.musicKeywords.join(', ')}`
                    : '-'}
              </span>
            </li>
          </ul>

          {phase === 'ready' && !inPreview ? (
            <button className="kmg-apply" onClick={showResult}>Show the result</button>
          ) : (
            <div className="kmg-preview-actions">
              <p className="kmg-hint">Previewing the AI edit on your video.</p>
              <div className="kmg-preview-btns">
                <button className="kmg-rollback" onClick={rollback}>Roll back</button>
                <button className="kmg-apply small" onClick={applyResult}>Apply & save</button>
              </div>
            </div>
          )}
          <button className="kmg-redo" onClick={runAgain}>Run again</button>
        </div>
      )}
    </div>
  );
}

/** The brand petal-flower mark — the main app icon, also used as kMagic's tab
 *  icon. Five gradient petals, no outline. */
export function KmagicMark({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden className="kmg-mark">
      <defs>
        <radialGradient id="kmgPetal" cx="0.5" cy="1" r="1">
          <stop offset="0" stopColor="#ff9a8b" />
          <stop offset="0.45" stopColor="#ff6fae" />
          <stop offset="0.78" stopColor="#a06bff" />
          <stop offset="1" stopColor="#42c6ff" />
        </radialGradient>
        {/* One petal, tip up, base overlapping the flower centre (12,12). */}
        <path id="kmgLeaf" d="M12 13 C8 10.4 8 4.2 12 1.6 C16 4.2 16 10.4 12 13 Z" />
      </defs>
      <g fill="url(#kmgPetal)">
        <use href="#kmgLeaf" />
        <use href="#kmgLeaf" transform="rotate(72 12 12)" />
        <use href="#kmgLeaf" transform="rotate(144 12 12)" />
        <use href="#kmgLeaf" transform="rotate(216 12 12)" />
        <use href="#kmgLeaf" transform="rotate(288 12 12)" />
      </g>
    </svg>
  );
}
